import React, { useState, useEffect } from 'react';
import { 
  CreditCard, 
  Loader, 
  ArrowLeft, 
  Shield,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import api from '../utils/api';

interface RazorpayPaymentProps {
  orderId: string;
  amount: number;
  onSuccess: (paymentDetails: any) => void;
  onError: (error: string) => void;
  onBack: () => void;
}

// Declare Razorpay in global scope
declare global {
  interface Window {
    Razorpay: any;
  }
}

const RazorpayPayment: React.FC<RazorpayPaymentProps> = ({ 
  orderId, 
  amount, 
  onSuccess, 
  onError, 
  onBack 
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [paymentData, setPaymentData] = useState<any>(null);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    // Load Razorpay script
    const loadRazorpayScript = () => {
      return new Promise((resolve) => {
        if (window.Razorpay) {
          setIsScriptLoaded(true);
          resolve(true);
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => {
          setIsScriptLoaded(true);
          resolve(true);
        };
        script.onerror = () => {
          setError('Failed to load Razorpay. Please check your internet connection.');
          resolve(false);
        };
        document.body.appendChild(script);
      });
    };

    loadRazorpayScript();
  }, []);

  const initiatePayment = async () => {
    if (!isScriptLoaded) {
      setError('Razorpay is still loading. Please wait.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Get payment configuration from backend
      const response = await api.post('/payment/initiate-upi', {
        orderId,
        amount,
        paymentType: 'web' // Razorpay web checkout
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to initiate payment');
      }

      const { params } = response.data;
      setPaymentData(params);

      // Configure Razorpay options
      const options = {
        ...params,
        handler: async (response: any) => {
          try {
            // Verify payment on backend
            const verifyResponse = await api.post('/payment/razorpay/callback', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });

            if (verifyResponse.data.success) {
              onSuccess({
                paymentId: response.razorpay_payment_id,
                orderId: response.razorpay_order_id,
                signature: response.razorpay_signature
              });
            } else {
              onError('Payment verification failed');
            }
          } catch (error: any) {
            console.error('Payment verification error:', error);
            onError(error.response?.data?.message || 'Payment verification failed');
          }
        },
        modal: {
          ondismiss: () => {
            console.log('Payment modal closed by user');
            setIsLoading(false);
          }
        }
      };

      // Create and open Razorpay checkout
      const rzp = new window.Razorpay(options);
      
      rzp.on('payment.failed', (response: any) => {
        console.error('Payment failed:', response.error);
        onError(response.error.description || 'Payment failed');
        setIsLoading(false);
      });

      rzp.open();
      
    } catch (error: any) {
      console.error('Payment initiation error:', error);
      setError(error.response?.data?.message || error.message || 'Failed to initiate payment');
    } finally {
      setIsLoading(false);
    }
  };

  const retryPayment = () => {
    setError('');
    initiatePayment();
  };

  return (
    <div className="max-w-md mx-auto bg-white dark:bg-secondary-800 rounded-lg shadow-lg p-6">
      {/* Header */}
      <div className="flex items-center mb-6">
        <button
          onClick={onBack}
          className="flex items-center text-gray-600 dark:text-gray-400 hover:text-primary-600 mr-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </button>
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
          Razorpay Payment
        </h2>
      </div>

      {/* Payment Summary */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Amount to Pay:</span>
          <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">₹{amount.toLocaleString()}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600 dark:text-gray-400">Order ID:</span>
          <span className="text-sm font-mono text-gray-900 dark:text-gray-100">{orderId}</span>
        </div>
      </div>

      {/* Security Badge */}
      <div className="flex items-center justify-center mb-6">
        <div className="flex items-center bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 px-3 py-2 rounded-full text-sm">
          <Shield className="h-4 w-4 mr-2" />
          <span className="font-medium">Secure Payment by Razorpay</span>
        </div>
      </div>

      {/* Payment Methods Info */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Supported Payment Methods:</h3>
        <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 dark:text-gray-400">
          <div className="flex items-center">
            <CheckCircle className="h-3 w-3 text-green-500 mr-1" />
            Credit/Debit Cards
          </div>
          <div className="flex items-center">
            <CheckCircle className="h-3 w-3 text-green-500 mr-1" />
            UPI (GPay, PhonePe, etc.)
          </div>
          <div className="flex items-center">
            <CheckCircle className="h-3 w-3 text-green-500 mr-1" />
            Net Banking
          </div>
          <div className="flex items-center">
            <CheckCircle className="h-3 w-3 text-green-500 mr-1" />
            Digital Wallets
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <XCircle className="h-5 w-5 text-red-500 mr-2 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-red-800 dark:text-red-200 mb-1">
                Payment Error
              </h4>
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          </div>
          <button
            onClick={retryPayment}
            disabled={isLoading}
            className="mt-3 w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:bg-gray-400"
          >
            Retry Payment
          </button>
        </div>
      )}

      {/* Loading State */}
      {!isScriptLoaded && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <Loader className="h-4 w-4 text-blue-600 animate-spin mr-2" />
            <span className="text-sm text-blue-700 dark:text-blue-300">Loading Razorpay...</span>
          </div>
        </div>
      )}

      {/* Payment Button */}
      <button
        onClick={initiatePayment}
        disabled={isLoading || !isScriptLoaded || !!error}
        className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white py-3 px-6 rounded-lg font-medium transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
      >
        {isLoading ? (
          <>
            <Loader className="h-5 w-5 animate-spin" />
            <span>Processing...</span>
          </>
        ) : (
          <>
            <CreditCard className="h-5 w-5" />
            <span>Pay ₹{amount.toLocaleString()}</span>
          </>
        )}
      </button>

      {/* Footer Info */}
      <div className="mt-4 text-center">
        <div className="flex items-center justify-center text-xs text-gray-500 dark:text-gray-400">
          <AlertCircle className="h-3 w-3 mr-1" />
          <span>Powered by Razorpay • Your payment information is secure</span>
        </div>
      </div>

      {/* Debug Info (only in development) */}
      {process.env.NODE_ENV === 'development' && paymentData && (
        <div className="mt-6 p-3 bg-gray-100 dark:bg-secondary-700 rounded text-xs">
          <details>
            <summary className="cursor-pointer font-mono">Debug Info</summary>
            <pre className="mt-2 overflow-auto">{JSON.stringify(paymentData, null, 2)}</pre>
          </details>
        </div>
      )}
    </div>
  );
};

export default RazorpayPayment;